import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ApiCall from "../../common/ApiCall";
import ToastAlert from "../../common/ToastAlert";
import { countries } from "../../common/Config";
import { setEmailVerified, setLoading, setLogin } from "../../redux/user/user.action";
import { setBookunWebhook, setFareharborWebhook } from "../../redux/global/global.action";

const STEP_FIELDS = {
    bokun: [
        { name: 'bokun_company_id', label: 'Company ID', placeholder: 'Enter Bokun company ID' },
    ],
    fareharbor: [
        { name: 'fareharbor_company_shortname', label: 'Company Shortname', placeholder: 'Enter FareHarbor company shortname' },
    ],
    pluralo: [
        { name: 'integration_key', label: 'Integration Key', placeholder: 'Enter Pluralo integration key from Octo' },
    ],
    turitop: [
        { name: 'turitop_company_id', label: 'Company short id', placeholder: 'Enter Turitop company ID' },
    ],
};

const PROVIDER_NAMES = { bokun: 'Bokun', fareharbor: 'FareHarbor', pluralo: 'Pluralo', turitop: 'Turitop' };

const emptyAccount = {
    code: '', first_name: '', last_name: '', email: '', legal_company_name: '',
    company_country: 'PT', password: '', cpassword: '',
};

export default function ProviderRegisterModal({ provider, onClose }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [creds, setCreds] = useState({});
    const [account, setAccount] = useState(emptyAccount);
    const [pending, setPending] = useState({ token: '', mode: 'otp' });
    const [errors, setErrors] = useState({});
    const [inUse, setInUse] = useState(false);
    const [busy, setBusy] = useState(false);

    // reset whenever a different provider is opened
    useEffect(() => {
        setStep(1);
        setCreds({});
        setAccount(emptyAccount);
        setPending({ token: '', mode: 'otp' });
        setErrors({});
        setInUse(false);
    }, [provider]);

    if (!provider) return null;
    const fields = STEP_FIELDS[provider];
    const providerName = PROVIDER_NAMES[provider];

    const start = async (e) => {
        e.preventDefault();
        if (busy) return;
        const missing = fields.find((f) => !String(creds[f.name] || '').trim());
        if (missing) {
            setErrors({ [missing.name]: `${missing.label} is required.` });
            return;
        }
        setErrors({});
        setInUse(false);
        setBusy(true);
        dispatch(setLoading(true));
        const res = await ApiCall('post', 'register-provider/start', { provider, ...creds });
        dispatch(setLoading(false));
        setBusy(false);

        if (res?.data?.success) {
            const prefill = res.data.prefill || {};
            setPending({ token: res.data.token, mode: res.data.mode });
            const prefillCountry = String(prefill.country || '').trim();
            const matchedCountry = countries.find((c) =>
                c.code.toLowerCase() === prefillCountry.toLowerCase() || c.name.toLowerCase() === prefillCountry.toLowerCase()
            );
            setAccount((a) => ({
                ...a,
                email: prefill.email || a.email,
                legal_company_name: prefill.company_name || a.legal_company_name,
                company_country: matchedCountry ? matchedCountry.code : a.company_country,
            }));
            ToastAlert({ msg: res.data.message, msgType: 'success' });
            setStep(2);
        } else if (res?.data?.code === 'already_in_use') {
            setInUse(true);
        } else {
            ToastAlert({ msg: res?.data?.message || 'Something went wrong', msgType: 'error' });
        }
    };

    const complete = async (e) => {
        e.preventDefault();
        if (busy) return;
        const err = {};
        if (!account.code.trim()) err.code = pending.mode === 'otp' ? 'Code is required.' : 'Bokun product id is required.';
        if (!account.first_name.trim()) err.first_name = 'First name is required.';
        if (!account.last_name.trim()) err.last_name = 'Last name is required.';
        if (!account.email.trim()) err.email = 'Email is required.';
        if (!account.legal_company_name.trim()) err.legal_company_name = 'Company name is required.';
        if (account.password.length < 8) err.password = 'The password must contain a minimum of 8 characters.';
        if (account.cpassword !== account.password) err.cpassword = 'Password and confirm password must be same.';
        setErrors(err);
        if (Object.keys(err).length) return;

        setBusy(true);
        dispatch(setLoading(true));
        const res = await ApiCall('post', 'register-provider/complete', {
            token: pending.token,
            code: account.code.trim(),
            first_name: account.first_name,
            last_name: account.last_name,
            email: account.email,
            legal_company_name: account.legal_company_name,
            company_country: account.company_country,
            password: account.password,
        });
        dispatch(setLoading(false));
        setBusy(false);

        if (res?.data?.success) {
            dispatch(setEmailVerified(true));
            dispatch(setLogin({
                userDetails: res.data.user,
                token: res.data.access_token,
                tempEmail: res.data.user.email,
                supplierDetails: res.data.supplier,
            }));
            dispatch(setBookunWebhook(res.data?.isBokunWebhookSetup));
            dispatch(setFareharborWebhook(res.data?.isFareharborWebhookSetup));
            ToastAlert({ msg: res.data.message, msgType: 'success' });
            navigate('/dashboard');
        } else if (res?.data?.code === 'already_in_use') {
            setStep(1);
            setInUse(true);
        } else if (res?.data?.errors) {
            setErrors(res.data.errors);
        } else {
            ToastAlert({ msg: res?.data?.message || 'Something went wrong', msgType: 'error' });
            if (res?.data?.message?.includes('start again')) setStep(1);
        }
    };

    const setAcc = (e) => setAccount({ ...account, [e.target.name]: e.target.value });
    const fieldErr = (name) => errors[name] && <span className="cus-err">{errors[name]}</span>;

    return (
        <Modal show={!!provider} onHide={onClose} centered className="su-modal">
            <Modal.Header closeButton>
                <Modal.Title>Register with {providerName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {inUse && (
                    <div className="su-inuse">
                        This {providerName} account is already in use. Please <Link to="/login">login</Link>.
                    </div>
                )}

                {step === 1 && (
                    <form onSubmit={start}>
                        {fields.map((f) => (
                            <div className="sq-input-wrap" key={f.name}>
                                <label className="sq-label">{f.label}</label>
                                <input className="sq-input no-icon" name={f.name} value={creds[f.name] || ''} placeholder={f.placeholder}
                                    onChange={(e) => setCreds({ ...creds, [e.target.name]: e.target.value })} />
                                {fieldErr(f.name)}
                            </div>
                        ))}
                        <button className="common-btn sq-btn-primary" type="submit" disabled={busy}>
                            {busy ? 'Loading...' : 'Connect'}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={complete}>
                        <div className="sq-input-wrap">
                            <label className="sq-label">{pending.mode === 'otp' ? 'Verification code' : 'Bokun product ID'}</label>
                            <input className="sq-input no-icon" name="code" value={account.code} onChange={setAcc}
                                placeholder={pending.mode === 'otp' ? 'Enter the 6 digit code sent to your provider email' : 'Enter any Bokun product ID that is in contract'} />
                            {fieldErr('code')}
                        </div>
                        <div className="su-grid">
                            <div className="sq-input-wrap">
                                <label className="sq-label">First Name</label>
                                <input className="sq-input no-icon" name="first_name" value={account.first_name} onChange={setAcc} placeholder="Enter first name" />
                                {fieldErr('first_name')}
                            </div>
                            <div className="sq-input-wrap">
                                <label className="sq-label">Last Name</label>
                                <input className="sq-input no-icon" name="last_name" value={account.last_name} onChange={setAcc} placeholder="Enter last name" />
                                {fieldErr('last_name')}
                            </div>
                            <div className="sq-input-wrap">
                                <label className="sq-label">Email</label>
                                <input className="sq-input no-icon" name="email" type="email" value={account.email} onChange={setAcc} placeholder="Enter your email" />
                                {fieldErr('email')}
                            </div>
                            <div className="sq-input-wrap">
                                <label className="sq-label">Company Name</label>
                                <input className="sq-input no-icon" name="legal_company_name" value={account.legal_company_name} onChange={setAcc} placeholder="Enter company name" />
                                {fieldErr('legal_company_name')}
                            </div>
                            <div className="sq-input-wrap su-full">
                                <label className="sq-label">Company Country</label>
                                <select className="sq-input no-icon" name="company_country" value={account.company_country} onChange={setAcc}>
                                    {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                                </select>
                                {fieldErr('company_country')}
                            </div>
                            <div className="sq-input-wrap">
                                <label className="sq-label">Password</label>
                                <input className="sq-input no-icon" name="password" type="password" value={account.password} onChange={setAcc} placeholder="Enter password" />
                                {fieldErr('password')}
                            </div>
                            <div className="sq-input-wrap">
                                <label className="sq-label">Confirm Password</label>
                                <input className="sq-input no-icon" name="cpassword" type="password" value={account.cpassword} onChange={setAcc} placeholder="Confirm password" />
                                {fieldErr('cpassword')}
                            </div>
                        </div>
                        <button className="common-btn sq-btn-primary" type="submit" disabled={busy}>
                            {busy ? 'Loading...' : 'Create account'}
                        </button>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}
